import { makeAutoObservable, runInAction } from 'mobx'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { logger } from 'shared/lib'
import { showToast } from 'shared/ui'
import { type IAddMemberEntry, type IAddMemberStoreConfig } from 'widgets/AddMembers/store/types'
import {
  AddNewMembersModalContent,
  type IAddNewMembersModalContentProps,
} from 'widgets/AddMembers/ui/AddNewMembersModalContent/AddNewMembersModalContent'

const DEFAULT_MODAL_ID = 'add-new-members-modal'
const DEFAULT_SUCCESS_MESSAGE = 'Members added'

export class AddMemberStore<TEntry extends IAddMemberEntry = IAddMemberEntry> {
  private _search = ''
  private _selectedMembers: TEntry[] = []
  private _loading = false

  constructor(private readonly _config: IAddMemberStoreConfig<TEntry>) {
    makeAutoObservable(this)
  }

  private get _modalId() {
    return this._config.modalId ?? DEFAULT_MODAL_ID
  }

  get noUsedMembers(): TEntry[] {
    const existingIds = new Set(this._config.getExistingMemberIds())
    return this._config.getOrganizationMembers().filter((m) => !existingIds.has(m.id))
  }

  get filteredMembers(): TEntry[] {
    if (!this._search.trim()) return this.noUsedMembers
    const term = this._search.toLowerCase().trim()
    return this.noUsedMembers.filter((m) => m.full_name.toLowerCase().includes(term))
  }

  get organizationMembersLoading() {
    return this._config.getOrganizationMembersLoading()
  }

  get search() {
    return this._search
  }

  get modalId() {
    return this._modalId
  }

  get selectedMembers() {
    return this._selectedMembers
  }

  get selectedLength() {
    return this._selectedMembers.length
  }

  get isAllSelected(): boolean {
    if (this.filteredMembers.length === 0) return false
    return this.filteredMembers.every((m) => this.isMemberSelected(m))
  }

  get selectAllButtonText() {
    return this.isAllSelected ? 'Deselect all' : 'Select all'
  }

  get addButtonText() {
    if (this.selectedLength === 0) return 'Add member'
    return `Add ${this.selectedLength} member${this.selectedLength !== 1 ? 's' : ''}`
  }

  get hasNoSearchResults() {
    return this._search.trim() !== '' && this.filteredMembers.length === 0
  }

  get hasNoAvailableMembers() {
    return this.noUsedMembers.length === 0
  }

  get shouldShowEmptyState() {
    return this.hasNoAvailableMembers || this.hasNoSearchResults
  }

  get emptyStateMessage() {
    if (this.hasNoAvailableMembers) return 'No members available'
    if (this.hasNoSearchResults) return 'No results found'
    return ''
  }

  get loading() {
    return this._loading
  }

  toggleSelectAll = () => {
    if (this.isAllSelected) {
      this._selectedMembers = this._selectedMembers.filter(
        (s) => !this.filteredMembers.some((m) => m.id === s.id)
      )
    } else {
      const toAdd = this.filteredMembers.filter((m) => !this.isMemberSelected(m))
      this._selectedMembers = [...this._selectedMembers, ...toAdd]
    }
  }

  selectMember = (member: TEntry) => {
    if (this.isMemberSelected(member)) {
      this._selectedMembers = this._selectedMembers.filter((m) => m.id !== member.id)
    } else {
      this._selectedMembers = [...this._selectedMembers, member]
    }
  }

  isMemberSelected = (member: TEntry) => {
    return this._selectedMembers.some((m) => m.id === member.id)
  }

  clearSelection = () => {
    this._selectedMembers = []
  }

  deselectAll = () => {
    this._selectedMembers = []
  }

  setSearch = (search: string) => {
    this._search = search
  }

  addNewMembers = async () => {
    if (this._selectedMembers.length === 0) return

    this._loading = true
    try {
      const result = await this._config.submitAddMembers(this._selectedMembers)

      if (
        !!result &&
        typeof result === 'object' &&
        result.chunksCount !== null &&
        result.chunksCount !== undefined &&
        this._config.waitForBulk
      ) {
        await this._config.waitForBulk(result.chunksCount)
      }

      this.closeModalHandler()
      this._selectedMembers = []

      showToast({
        title: this._config.successMessage ?? DEFAULT_SUCCESS_MESSAGE,
        type: 'success',
      })

      await this._config.onSuccess?.()
    } catch (error) {
      logger.error(error)
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  clearStore = () => {
    this._config.onClear?.()
    this._search = ''
    this._selectedMembers = []
    this._loading = false
  }

  closeModalHandler = () => {
    this.clearStore()
    modalStore.removeModal(this._modalId)
  }

  closeModal = () => {
    modalStore.removeModal(this._modalId)
  }

  openAddMembersModal = () => {
    modalStore.addModal<IAddNewMembersModalContentProps<TEntry>>({
      id: this._modalId,
      title: 'Add members',
      width: 480,
      fullHeight: true,
      showCloseButton: false,
      paddingContent: '10px 24px 24px',
      onClose: this.closeModalHandler,
      ModalContentProps: {
        store: this,
      },
      ModalContent: AddNewMembersModalContent,
    })
  }
}
